import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { AdminLayout } from '../../../layouts/admin-layout'
import { SubscriptionEventsFeed } from '../../../components/admin/subscription-events-feed'
import { Badge } from '../../../components/ui/badge'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  admin: boolean
  super_admin?: boolean
  created_at: string
}

interface Project {
  id: number
  name: string
  created_at: string
  user: User
  author_email?: string
}

interface Stats {
  total_users: number
  new_users_this_week: number
  new_users_this_month: number
  total_organizations: number
  orgs_with_subscription: number
  free_orgs: number
  total_projects: number
  projects_this_week: number
  active_projects: number
  running_instances: number
  total_instances: number
  stopped_instances: number
}

interface RevenueStats {
  mrr: number
  total_subscribers: number
  subscribers_by_plan: {
    standard: number
    pro: number
    team: number
  }
  mrr_by_plan: {
    standard: number
    pro: number
    team: number
  }
}

interface SubscriptionEvent {
  id: number
  event_type: string
  organization_name: string
  organization_id: number | null
  plan_name: string | null
  created_at: string
  details: string | null
  price_usd?: number | null
  billing_interval?: string | null
  from_plan?: string | null
  to_plan?: string | null
  from_price_usd?: number | null
  to_price_usd?: number | null
  from_billing_interval?: string | null
  to_billing_interval?: string | null
  status_transition?: string | null
  ltv?: number | null
}

interface MetricData {
  current: number
  previous: number
  daily: number[]
}

interface ActivityStats {
  messages: MetricData
  credits_used: MetricData
  user_messages: MetricData
  deployments: MetricData
}

interface CollectiveKnowledgeStats {
  manuals_count: number
  manuals_usage: number
  gotchas_count: number
  gotchas_injections: number
  gotchas_triggers: number
  skills_count: number
  skills_usage: number
}

interface DeploymentStats {
  total_successful: number
  this_week: number
  success_rate: number
}

interface ExceptionStats {
  total_caught: number
  auto_fixed: number
  auto_fix_rate: number
}

interface AwsCostDay {
  date: string
  amount: number
}

interface AwsServiceCost {
  service: string
  amount: number
  percentage: number
}

interface AwsCostSummary {
  current_month_total: number
  previous_month_total: number
  daily_average: number
  month_over_month_change_pct: number
}

interface AwsCosts {
  daily_costs?: AwsCostDay[]
  service_breakdown?: AwsServiceCost[]
  summary?: AwsCostSummary
  cached_at?: string
  error?: string
}

interface AdminDashboardIndexProps {
  stats: Stats
  recent_users: User[]
  recent_projects: Project[]
  revenue_stats: RevenueStats
  subscription_events: SubscriptionEvent[]
  activity_stats: ActivityStats
  collective_knowledge_stats: CollectiveKnowledgeStats
  deployment_stats: DeploymentStats
  exception_stats: ExceptionStats
  aws_costs: AwsCosts
}

function SparklineBar({ daily }: { daily: number[] }) {
  const max = Math.max(...daily, 1)
  return (
    <div className="flex items-end gap-0.5 h-8 mt-2">
      {daily.map((value, i) => (
        <div
          key={i}
          className="flex-1 bg-primary/60 rounded-sm min-h-[2px]"
          style={{ height: `${(value / max) * 100}%` }}
        />
      ))}
    </div>
  )
}

function ActivityMetricCard({ metric, label, format }: { metric: MetricData; label: string; format?: 'currency' | 'number' }) {
  const pctChange = metric.previous === 0
    ? (metric.current > 0 ? 100 : 0)
    : ((metric.current - metric.previous) / metric.previous) * 100
  const isPositive = pctChange >= 0
  const displayValue = format === 'currency'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(metric.current)
    : new Intl.NumberFormat('en-US').format(metric.current)

  return (
    <Card className="shadow-none">
      <CardHeader className="pb-1">
        <CardDescription>{label}</CardDescription>
        <div className="flex items-baseline gap-2">
          <CardTitle className="text-3xl">{displayValue}</CardTitle>
          {metric.previous > 0 && (
            <span className={`text-xs font-medium ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {isPositive ? '▲' : '▼'} {Math.abs(pctChange).toFixed(0)}%
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <SparklineBar daily={metric.daily} />
      </CardContent>
    </Card>
  )
}

function Index({ stats, recent_users, recent_projects, revenue_stats, subscription_events, activity_stats, collective_knowledge_stats, deployment_stats, exception_stats, aws_costs }: AdminDashboardIndexProps) {
  const { t } = useTranslation('admin')

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) return t('dashboard.subscriptionActivity.time.justNow')
    if (diffInSeconds < 3600) return t('dashboard.subscriptionActivity.time.minutesAgo', { count: Math.floor(diffInSeconds / 60) })
    if (diffInSeconds < 86400) return t('dashboard.subscriptionActivity.time.hoursAgo', { count: Math.floor(diffInSeconds / 3600) })
    if (diffInSeconds < 604800) return t('dashboard.subscriptionActivity.time.daysAgo', { count: Math.floor(diffInSeconds / 86400) })
    if (diffInSeconds < 2592000) return t('dashboard.subscriptionActivity.time.weeksAgo', { count: Math.floor(diffInSeconds / 604800) })
    return date.toLocaleDateString()
  }

  const formatNumber = (n: number) => new Intl.NumberFormat('en-US').format(n)

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-2 py-2">
          <div className="px-4 lg:px-6">
            <Tabs defaultValue="overview" className="flex flex-col">
              <TabsContent value="overview">
                {/* Platform Stats Grid */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.platform.title')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card className="shadow-none">
                      <CardHeader className="pb-2">
                        <CardDescription>{t('dashboard.stats.totalUsers')}</CardDescription>
                        <CardTitle className="text-3xl">{stats.total_users}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.stats.newThisWeek')}</span>
                            <span className="font-medium text-green-600 dark:text-green-400">+{stats.new_users_this_week}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.stats.newThisMonth')}</span>
                            <span className="font-medium">+{stats.new_users_this_month}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader className="pb-2">
                        <CardDescription>{t('dashboard.stats.organizations')}</CardDescription>
                        <CardTitle className="text-3xl">{stats.total_organizations}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.stats.activeSubscriptions')}</span>
                            <Badge variant="secondary">{stats.orgs_with_subscription}</Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.stats.freeTier')}</span>
                            <Badge variant="outline">{stats.free_orgs}</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader className="pb-2">
                        <CardDescription>{t('dashboard.stats.totalProjects')}</CardDescription>
                        <CardTitle className="text-3xl">{stats.total_projects}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.stats.createdThisWeek')}</span>
                            <span className="font-medium text-green-600 dark:text-green-400">+{stats.projects_this_week}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.stats.activeNonArchived')}</span>
                            <span className="font-medium">{stats.active_projects}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader className="pb-2">
                        <CardDescription>{t('dashboard.stats.activeDevInstances')}</CardDescription>
                        <CardTitle className="text-3xl">{stats.running_instances}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.stats.totalInstances')}</span>
                            <span className="font-medium">{stats.total_instances}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.stats.stopped')}</span>
                            <span className="font-medium">{stats.stopped_instances}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Collective Knowledge */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.collectiveKnowledge.title')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="shadow-none">
                      <CardHeader className="pb-2">
                        <CardDescription>{t('dashboard.collectiveKnowledge.manuals')}</CardDescription>
                        <CardTitle className="text-3xl">{collective_knowledge_stats.manuals_count}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">{formatNumber(collective_knowledge_stats.manuals_usage)}</span> {t('dashboard.collectiveKnowledge.timesReferenced')}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader className="pb-2">
                        <CardDescription>{t('dashboard.collectiveKnowledge.gotchas')}</CardDescription>
                        <CardTitle className="text-3xl">{collective_knowledge_stats.gotchas_count}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.collectiveKnowledge.injections')}</span>
                            <span className="font-medium">{formatNumber(collective_knowledge_stats.gotchas_injections)}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.collectiveKnowledge.triggers')}</span>
                            <span className="font-medium">{formatNumber(collective_knowledge_stats.gotchas_triggers)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader className="pb-2">
                        <CardDescription>{t('dashboard.collectiveKnowledge.skills')}</CardDescription>
                        <CardTitle className="text-3xl">{collective_knowledge_stats.skills_count}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">{formatNumber(collective_knowledge_stats.skills_usage)}</span> {t('dashboard.collectiveKnowledge.timesUsed')}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Deployments + Self-Healing */}
                <div className="mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Deployments */}
                    <div>
                      <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.deployments.title')}</h2>
                      <div className="grid grid-cols-3 gap-4">
                        <Card className="shadow-none">
                          <CardHeader className="pb-2">
                            <CardDescription>{t('dashboard.deployments.totalSuccessful')}</CardDescription>
                            <CardTitle className="text-3xl">{formatNumber(deployment_stats.total_successful)}</CardTitle>
                          </CardHeader>
                        </Card>
                        <Card className="shadow-none">
                          <CardHeader className="pb-2">
                            <CardDescription>{t('dashboard.deployments.thisWeek')}</CardDescription>
                            <CardTitle className="text-3xl">{deployment_stats.this_week}</CardTitle>
                          </CardHeader>
                        </Card>
                        <Card className="shadow-none">
                          <CardHeader className="pb-2">
                            <CardDescription>{t('dashboard.deployments.successRate')}</CardDescription>
                            <CardTitle className="text-3xl text-green-700 dark:text-green-400">{deployment_stats.success_rate}%</CardTitle>
                          </CardHeader>
                        </Card>
                      </div>
                    </div>

                    {/* Self-Healing */}
                    <div>
                      <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.selfHealing.title')}</h2>
                      <div className="grid grid-cols-3 gap-4">
                        <Card className="shadow-none">
                          <CardHeader className="pb-2">
                            <CardDescription>{t('dashboard.selfHealing.exceptionsCaught')}</CardDescription>
                            <CardTitle className="text-3xl">{formatNumber(exception_stats.total_caught)}</CardTitle>
                          </CardHeader>
                        </Card>
                        <Card className="shadow-none">
                          <CardHeader className="pb-2">
                            <CardDescription>{t('dashboard.selfHealing.autoFixed')}</CardDescription>
                            <CardTitle className="text-3xl">{formatNumber(exception_stats.auto_fixed)}</CardTitle>
                          </CardHeader>
                        </Card>
                        <Card className="shadow-none">
                          <CardHeader className="pb-2">
                            <CardDescription>{t('dashboard.selfHealing.autoFixRate')}</CardDescription>
                            <CardTitle className="text-3xl text-green-700 dark:text-green-400">{exception_stats.auto_fix_rate}%</CardTitle>
                          </CardHeader>
                        </Card>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Users and Projects */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('dashboard.recentUsers.title')}</CardTitle>
                      <CardDescription>{t('dashboard.recentUsers.description')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {recent_users.length > 0 ? (
                        <div className="space-y-4">
                          {recent_users.map((user) => (
                            <div key={user.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 flex-shrink-0 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-semibold text-sm">
                                  {user.first_name[0]}{user.last_name[0]}
                                </div>
                                <div>
                                  <p className="text-sm font-medium">
                                    {user.first_name} {user.last_name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">{user.email}</p>
                                </div>
                              </div>
                              {user.admin && (
                                <Badge variant="info">
                                  {t('dashboard.badges.admin')}
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">{t('dashboard.recentUsers.empty')}</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('dashboard.recentProjects.title')}</CardTitle>
                      <CardDescription>{t('dashboard.recentProjects.description')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {recent_projects.length > 0 ? (
                        <div className="space-y-4">
                          {recent_projects.map((project) => (
                            <div key={project.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                              <div>
                                <p className="text-sm font-medium">{project.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  by {project.user?.email}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">{t('dashboard.recentProjects.empty')}</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="internal">
                {/* Revenue Stats Grid */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.revenue.title')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card className="shadow-none border-green-200 dark:border-green-900 bg-green-50/50 dark:bg-green-950/20">
                      <CardHeader className="pb-3">
                        <CardDescription>{t('dashboard.revenue.mrr')}</CardDescription>
                        <CardTitle className="text-3xl text-green-700 dark:text-green-400">
                          {formatCurrency(revenue_stats.mrr)}
                        </CardTitle>
                      </CardHeader>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader className="pb-3">
                        <CardDescription>{t('dashboard.revenue.activeSubscribers')}</CardDescription>
                        <CardTitle className="text-3xl">{revenue_stats.total_subscribers}</CardTitle>
                      </CardHeader>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader className="pb-2">
                        <CardDescription>{t('dashboard.revenue.subscribersByPlan')}</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.plans.standard')}</span>
                            <Badge variant="secondary">{revenue_stats.subscribers_by_plan.standard}</Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.plans.pro')}</span>
                            <Badge variant="secondary">{revenue_stats.subscribers_by_plan.pro}</Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.plans.team')}</span>
                            <Badge variant="secondary">{revenue_stats.subscribers_by_plan.team}</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader className="pb-2">
                        <CardDescription>{t('dashboard.revenue.mrrByPlan')}</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.plans.standard')}</span>
                            <span className="font-medium">{formatCurrency(revenue_stats.mrr_by_plan.standard)}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.plans.pro')}</span>
                            <span className="font-medium">{formatCurrency(revenue_stats.mrr_by_plan.pro)}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{t('dashboard.plans.team')}</span>
                            <span className="font-medium">{formatCurrency(revenue_stats.mrr_by_plan.team)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Platform Activity */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.activity.title')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <ActivityMetricCard metric={activity_stats.credits_used} label={t('dashboard.activity.creditsUsed')} format="currency" />
                    <ActivityMetricCard metric={activity_stats.messages} label={t('dashboard.activity.aiMessages')} />
                    <ActivityMetricCard metric={activity_stats.user_messages} label={t('dashboard.activity.userMessages')} />
                    <ActivityMetricCard metric={activity_stats.deployments} label={t('dashboard.activity.deployments')} />
                  </div>
                </div>

                {/* AWS Costs */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.awsCosts.title')}</h2>
                  {aws_costs?.error ? (
                    <Card className="shadow-none border-red-200 dark:border-red-900">
                      <CardContent className="py-4">
                        <p className="text-sm text-red-600 dark:text-red-400">{t('dashboard.awsCosts.error')}: {aws_costs.error}</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        <Card className="shadow-none border-orange-200 dark:border-orange-900 bg-orange-50/50 dark:bg-orange-950/20">
                          <CardHeader className="pb-3">
                            <CardDescription>{t('dashboard.awsCosts.currentMonth')}</CardDescription>
                            <CardTitle className="text-3xl text-orange-700 dark:text-orange-400">
                              {formatCurrency(aws_costs?.summary?.current_month_total ?? 0)}
                            </CardTitle>
                          </CardHeader>
                        </Card>

                        <Card className="shadow-none">
                          <CardHeader className="pb-3">
                            <CardDescription>{t('dashboard.awsCosts.previousMonth')}</CardDescription>
                            <CardTitle className="text-3xl">
                              {formatCurrency(aws_costs?.summary?.previous_month_total ?? 0)}
                            </CardTitle>
                          </CardHeader>
                        </Card>

                        <Card className="shadow-none">
                          <CardHeader className="pb-3">
                            <CardDescription>{t('dashboard.awsCosts.dailyAverage')}</CardDescription>
                            <CardTitle className="text-3xl">
                              ${(aws_costs?.summary?.daily_average ?? 0).toFixed(2)}
                            </CardTitle>
                          </CardHeader>
                        </Card>

                        <Card className="shadow-none">
                          <CardHeader className="pb-3">
                            <CardDescription>{t('dashboard.awsCosts.monthOverMonth')}</CardDescription>
                            <CardTitle className={`text-3xl ${(aws_costs?.summary?.month_over_month_change_pct ?? 0) <= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                              {(aws_costs?.summary?.month_over_month_change_pct ?? 0) > 0 ? '+' : ''}{aws_costs?.summary?.month_over_month_change_pct ?? 0}%
                            </CardTitle>
                          </CardHeader>
                        </Card>
                      </div>

                      {/* Daily Costs Chart + Service Breakdown */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <Card className="shadow-none lg:col-span-2">
                          <CardHeader>
                            <CardTitle className="text-base">{t('dashboard.awsCosts.dailyCosts')}</CardTitle>
                            <CardDescription>{t('dashboard.awsCosts.last30Days')}</CardDescription>
                          </CardHeader>
                          <CardContent>
                            {aws_costs?.daily_costs && aws_costs.daily_costs.length > 0 ? (
                              <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={aws_costs.daily_costs}>
                                  <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v: string) => v.slice(5)}
                                    interval={4}
                                  />
                                  <YAxis
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v: number) => `$${v}`}
                                    width={55}
                                  />
                                  <Tooltip
                                    content={({ active, payload, label }) => {
                                      if (!active || !payload?.length) return null
                                      return (
                                        <div className="bg-popover border rounded-lg px-3 py-2 text-sm shadow-md">
                                          <p className="text-muted-foreground">{label}</p>
                                          <p className="font-medium">${Number(payload[0].value).toFixed(2)}</p>
                                        </div>
                                      )
                                    }}
                                  />
                                  <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <p className="text-sm text-muted-foreground text-center py-8">{t('dashboard.awsCosts.noData')}</p>
                            )}
                          </CardContent>
                        </Card>

                        <Card className="shadow-none">
                          <CardHeader>
                            <CardTitle className="text-base">{t('dashboard.awsCosts.topServices')}</CardTitle>
                            <CardDescription>{t('dashboard.awsCosts.currentMonthBreakdown')}</CardDescription>
                          </CardHeader>
                          <CardContent>
                            {aws_costs?.service_breakdown && aws_costs.service_breakdown.length > 0 ? (
                              <div className="space-y-3">
                                {aws_costs.service_breakdown.map((svc) => (
                                  <div key={svc.service}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                      <span className="text-muted-foreground truncate mr-2" title={svc.service}>
                                        {svc.service.replace('Amazon ', '').replace('AWS ', '')}
                                      </span>
                                      <span className="font-medium whitespace-nowrap">${svc.amount.toFixed(2)}</span>
                                    </div>
                                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-primary/70 rounded-full"
                                        style={{ width: `${Math.min(svc.percentage, 100)}%` }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground text-center py-8">{t('dashboard.awsCosts.noData')}</p>
                            )}
                          </CardContent>
                        </Card>
                      </div>

                      {aws_costs?.cached_at && (
                        <p className="text-xs text-muted-foreground mt-3">
                          {t('dashboard.awsCosts.cachedAt', { time: new Date(aws_costs.cached_at).toLocaleString() })}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Subscription Events Feed */}
                <div className="mb-8">
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('dashboard.subscriptionActivity.title')}</CardTitle>
                      <CardDescription>{t('dashboard.subscriptionActivity.description')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <SubscriptionEventsFeed events={subscription_events} />
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsList className="mt-8 self-start">
                <TabsTrigger value="overview">{t('dashboard.tabs.overview')}</TabsTrigger>
                <TabsTrigger value="internal">{t('dashboard.tabs.internal')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
